// The hero figure: one merged geometry, built once and shared.
//
// A FIGURE, NOT A SOLID OF REVOLUTION.
//
// This was a lathe - a rotationally symmetric profile with a base, a waist,
// shoulders, a neck and a head. Nothing was wrong with it as a piece, but a
// lathe can never have ARMS, and arms are most of what makes a silhouette
// read as a person. From every angle it was a chess pawn.
//
// Built from primitives and MERGED, so a hero is still exactly one mesh and
// the draw-call cost is unchanged. At the size this is actually seen - a
// hero is about 23 screen pixels tall - the detail is irrelevant and the
// SILHOUETTE is everything: a head, a gap at each side for arms, and a gap
// between the legs.
//
// Its own module, with no React and no scene, so the SHAPE CONTRACT can be
// tested. The contract matters: mesh.position.y, the label height and the
// click raycast are all derived from HERO_SIZE, and if this geometry changes
// size they all break SILENTLY - the label floats, the raycast misses, and
// nothing says so.
//
// EVERY transform lives in here. A shared geometry is built once, so
// anything that changes it permanently must be part of building it; a
// transform left outside runs again on every scene rebuild and moves the
// same object further each time. That has bitten this codebase twice.

import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

/** How tall a hero is. The figure, its label and its hit box all derive
 *  from this one number. */
export const HERO_SIZE = 1.3;

/** Total height of the figure, which is what HERO_SIZE names. */
export const HERO_FIGURE_HEIGHT = HERO_SIZE * 2.4;

export function buildHeroGeometry(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  const put = (g: THREE.BufferGeometry, x: number, y: number, z = 0) => {
    g.translate(x, y, z);
    parts.push(g);
  };

  // A base, kept: a miniature stands on one, and it stops the figure
  // looking like it is sinking into the field. Deliberately much smaller
  // than the lathe's, which was 2.2 wide against a mob's 1.1.
  put(new THREE.CylinderGeometry(0.62, 0.70, 0.20, 12), 0, 0.10);
  // Legs, apart, so a gap reads between them.
  put(new THREE.BoxGeometry(0.24, 0.86, 0.30), -0.22, 0.63);
  put(new THREE.BoxGeometry(0.24, 0.86, 0.30), 0.22, 0.63);
  // Torso and hips as one block.
  put(new THREE.BoxGeometry(0.70, 1.00, 0.40), 0, 1.58);
  // Arms, held CLEAR of the body. The gap is what makes them read as arms
  // rather than as part of the torso.
  put(new THREE.BoxGeometry(0.20, 1.10, 0.26), -0.50, 1.58);
  put(new THREE.BoxGeometry(0.20, 1.10, 0.26), 0.50, 1.58);
  // Neck, then head. The crown lands on exactly HERO_FIGURE_HEIGHT.
  put(new THREE.CylinderGeometry(0.15, 0.15, 0.36, 8), 0, 2.26);
  put(new THREE.SphereGeometry(0.34, 12, 10), 0, 2.78);

  const merged = mergeGeometries(parts, false) ?? parts[0]!;
  for (const p of parts) if (p !== merged) p.dispose();
  // Centred on its own middle, because mesh.position.y = HERO_SIZE * 1.2 is
  // what puts a hero on the ground. A lathe is drawn from its base and this
  // from its feet; both need the shift, and forgetting it floats the
  // selection highlight.
  merged.translate(0, -HERO_FIGURE_HEIGHT / 2, 0);
  return merged;
}
